import is from '@sindresorhus/is';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import type { AuthorizationProvider } from '../authorization/authorization-provider';
import { is401Error, is403Error, is404Error, is429Error, isConsentError } from '../error-code/error-code';
import type { ErrorReporter } from '../http-fetcher/http-fetcher';
import { NetworkError } from '../http-fetcher/network-error';
import { claimNetworkErrorReport, takeNetworkErrorReportSlot } from '../http-fetcher/network-error-report';
import { consentQueryKey } from '../query-hooks/consent';

export const createQueryClient = (
    refetchOnWindowFocus: boolean,
    errorReporter: ErrorReporter,
    logout?: AuthorizationProvider['logout'],
): QueryClient => {
    // A 401 is retried because the token renewal behind it may have failed for a transient reason; a
    // revoked session is already being logged out by the fetcher.
    const isRetryable = (error: unknown): boolean => !is403Error(error) && !is404Error(error);

    // The fetcher defers to whoever owns the retries, so this is the single report for a
    // logical operation that never recovered — claimed here so the fetcher's fallback,
    // which runs a macrotask later, stays silent about the attempt that just failed.
    const reportTerminalNetworkError = (error: unknown, attempts: number): void => {
        claimNetworkErrorReport(error);

        if (!(error instanceof NetworkError) || !takeNetworkErrorReportSlot(error, 'terminal')) {
            return;
        }

        errorReporter.reportError(error.message, error, {
            network: {
                attempts,
                cause: is.error(error.cause) ? error.cause.message : String(error.cause),
                method: error.method,
                url: error.url,
            },
        });
    };

    const queryCache = new QueryCache({
        onError: async (error: unknown, query): Promise<void> => {
            reportTerminalNetworkError(error, query.state.fetchFailureCount);

            if (isConsentError(error)) {
                return queryClient.refetchQueries({ queryKey: consentQueryKey });
            }

            if (is401Error(error)) {
                logout?.();

                return;
            }
        },
    });

    const mutationCache = new MutationCache({
        onError: (error: unknown, _variables, _context, mutation): void => {
            // Unlike a query, a mutation dispatches its error state after this hook runs,
            // so the attempt that just failed is not counted yet.
            reportTerminalNetworkError(error, mutation.state.failureCount + 1);
        },
    });

    // An attempt that will be retried never reaches `onError`, and a `retry` callback is not
    // invoked at all for a numeric or boolean `retry`. The retryer dispatches `failed` from its
    // rejection microtask instead — before any retry delay and whatever `retry` is configured
    // to — so claiming from the cache event beats the fetcher's macrotask fallback everywhere.
    queryCache.subscribe((event) => {
        if (event.type === 'updated' && event.action.type === 'failed') {
            claimNetworkErrorReport(event.action.error);
        }
    });

    mutationCache.subscribe((event) => {
        if (event.type === 'updated' && event.action.type === 'failed') {
            claimNetworkErrorReport(event.action.error);
        }
    });

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                networkMode: 'always',
                refetchOnWindowFocus,
                retry: (failureCount, error) => failureCount < 2 && isRetryable(error),
                throwOnError: (error: unknown) => !isConsentError(error) && !is401Error(error) && !is429Error(error),
            },
        },
        mutationCache,
        queryCache,
    });

    return queryClient;
};
